import {ScaleLinear} from 'd3';
import React from 'react';

import {PolymerLayout, SliceMapType} from 'constants/MapsConstants';
import FileStates from 'FileStates';
import {BioentityPopoverContent} from 'molbio/BioentityPopoverContent';
import {
  LINK_HIGHLIGHT_STROKE_OPACITY,
  LINK_HIGHLIGHT_STROKE_WIDTH,
} from 'molbio/maps/BiointelligentLinksConstants';
import {
  getHighlightColor,
  getHighlightOpacity,
  getRectangularPath,
} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import {PartHighlightRegion} from 'molbio/maps/translateLinksToHighlightRegions';
import Type from 'molbio/maps/Type';
import * as sequtil from 'molbio/SeqUtil';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Popover from 'ui-library/floating-ui/Popover';

function getPartLabelText(partRegion: PartHighlightRegion): string {
  if (partRegion.isArchived) {
    return `${partRegion.name} (archived)`;
  } else {
    return partRegion.name;
  }
}

function getNameRelativeX(
  partRegion: PartHighlightRegion,
  type: Type,
  sequenceLength: number,
  xFn: ScaleLinear<number, number>,
  labelText: string
): number {
  const start = partRegion.start;
  const end = start + sequtil.length(start, partRegion.end, sequenceLength);
  const startX = xFn(start);
  const endX = xFn(end);
  return (endX + startX - type.width(labelText.length)) / 2;
}

export interface LegacyNucleotidePartsViewProps {
  polymerLayout: PolymerLayout;
  partRegions: Array<PartHighlightRegion>;
  xFn: ScaleLinear<number, number>;
  sliceStart: number;
  sliceEnd: number;
  backgroundY: number;
  height: number;
  sequenceLength: number;
  type: Type;
  mapType: SliceMapType;
}

/**
 * This is 'legacy' because it only applies when overlapping parts have not been enabled.
 *
 * Most customers do not yet have overlapping parts enabled, but we want to enable them everywhere and switch
 * everyone over to the newer BiointelligentLinkHighlightView.
 */
export default function LegacyNucleotidePartsView({
  polymerLayout,
  partRegions,
  xFn,
  sliceStart,
  sliceEnd,
  backgroundY,
  height,
  sequenceLength,
  type,
  mapType,
}: LegacyNucleotidePartsViewProps): JSX.Element {
  return (
    <ComponentWrapper layout={polymerLayout} shouldStopMouseDownPropagation={false}>
      <>
        {partRegions.map((partRegion: PartHighlightRegion, index: number) => {
          const labelText = getPartLabelText(partRegion);
          const startX = xFn(partRegion.start);
          const nameRelativeX = getNameRelativeX(partRegion, type, sequenceLength, xFn, labelText);

          return (
            <g key={`${partRegion.name}-${index}`}>
              <path
                d={getRectangularPath(
                  partRegion,
                  sliceStart,
                  sliceEnd,
                  xFn,
                  backgroundY,
                  height,
                  sequenceLength
                )}
                // In legacy parts view, the hover status of parts is not relevant for their display.
                stroke={getHighlightColor({
                  region: partRegion,
                  highlightedStrand: 0,
                  hoveredLinkIndices: null,
                  complementVisible: true,
                  forLegacyParts: true,
                })}
                strokeWidth={LINK_HIGHLIGHT_STROKE_WIDTH}
                strokeOpacity={LINK_HIGHLIGHT_STROKE_OPACITY}
                fill={getHighlightColor({
                  region: partRegion,
                  highlightedStrand: 0,
                  hoveredLinkIndices: null,
                  complementVisible: true,
                  forLegacyParts: true,
                })}
                fillOpacity={getHighlightOpacity(partRegion, false)}
              />
              {partRegion.partRefSequenceId != null && (
                <TooltipWrapper tooltipTitle={mapType === SliceMapType.minimap ? labelText : null}>
                  <Popover
                    renderContent={() => (
                      <BioentityPopoverContent
                        // When there are no overlapping part regions, the partRef will always be defined.
                        id={partRegion.partRefSequenceId}
                        onClickName={() => {
                          FileStates.get().openFolderItem(partRegion.partRefSequenceId);
                        }}
                        validationExtras={null}
                      />
                    )}
                    isDisabled={mapType === SliceMapType.minimap}
                  >
                    <text
                      fontSize={type.fontSize}
                      dx={nameRelativeX < startX ? startX : nameRelativeX}
                      dy={type.height()}
                    >
                      {nameRelativeX < startX ? '' : labelText}
                    </text>
                  </Popover>
                </TooltipWrapper>
              )}
            </g>
          );
        })}
      </>
    </ComponentWrapper>
  );
}
